import { supabase } from "@shared/lib/supabase/client";
import type {
  Filter,
  GetListResponse,
  TableName,
  TableRow,
  UseListProps,
} from "@shared/types/query";
import { useQuery } from "@tanstack/react-query";

const encodeFilters = (filters: Filter[]) =>
  filters.map((f) => `${f.field}:${f.operator}:${String(f.value)}`).sort();

export function useList<T extends TableName>({
  resource,
  pagination = { current: 1, pageSize: 10 },
  sorters = [],
  filters = [],
  queryOptions,
}: UseListProps<T>) {
  const { current, pageSize } = pagination;
  const from = (current - 1) * pageSize;
  const to = from + pageSize - 1;

  return useQuery<GetListResponse<TableRow<T>>>({
    queryKey: [
      resource,
      "list",
      pagination.current,
      pagination.pageSize,
      encodeFilters(filters),
      sorters.map((s) => `${s.field}:${s.order}`).sort(),
    ],
    queryFn: async () => {
      let query = supabase
        .from(resource)
        .select("*", { count: "exact" })
        .range(from, to);

      for (const sorter of sorters) {
        query = query.order(sorter.field as never, {
          ascending: sorter.order === "asc",
        });
      }

      for (const filter of filters) {
        const { field, operator, value } = filter;

        switch (operator) {
          case "eq":
            query = query.eq(field as never, value as never);
            break;
          case "neq":
            query = query.neq(field as never, value as never);
            break;
          case "gt":
            query = query.gt(field as never, value as never);
            break;
          case "gte":
            query = query.gte(field as never, value as never);
            break;
          case "lt":
            query = query.lt(field as never, value as never);
            break;
          case "lte":
            query = query.lte(field as never, value as never);
            break;
          case "like":
            query = query.like(field as never, `%${value}%` as never);
            break;
          case "ilike":
            query = query.ilike(field as never, `%${value}%` as never);
            break;
          case "is":
            query = query.is(field as never, value as never);
            break;
          case "in":
            query = query.in(field as never, value as never);
            break;
        }
      }

      const result = await query;

      const { data, error, count } = result as {
        data: TableRow<T>[] | null;
        error: typeof result.error;
        count: number | null;
      };

      if (error) throw error;

      return {
        data: data ?? [],
        total: count ?? 0,
      };
    },
    placeholderData: (prev) => prev,
    ...queryOptions,
  });
}
